import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Wallet, Search, ChevronDown, ChevronUp } from "lucide-react";
import { Toaster } from "sonner";

const TYPES = [
    { v: "all",         label: "All" },
    { v: "doctor",      label: "Doctors" },
    { v: "distributor", label: "Distributors" },
    { v: "retailer",    label: "Retailers" },
    { v: "patient",     label: "Patients" },
    { v: "other",       label: "Other" },
];

export default function AccountsOutstanding() {
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("all");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const params = tab !== "all" ? { customer_type: tab } : {};
            const { data } = await api.get("/accounts/outstanding", { params });
            setData(data);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    const rows = useMemo(() => {
        if (!data) return [];
        const needle = q.trim().toLowerCase();
        if (!needle) return data.rows;
        return data.rows.filter((r) =>
            (r.name || "").toLowerCase().includes(needle)
            || (r.phone || "").toLowerCase().includes(needle)
            || (r.city || "").toLowerCase().includes(needle));
    }, [data, q]);

    return (
        <div data-testid="accounts-outstanding">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Wallet className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Outstanding Receivables</h1>
                    <p className="text-sm text-stone-600 mt-2">Customers with unpaid bills, broken into aging buckets. Click a row to see individual bills.</p>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer…" className="rounded-none border-stone-300 pl-9 w-72" data-testid="out-search" />
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList className="rounded-none bg-white border border-sky-100 p-0 h-auto flex flex-wrap">
                    {TYPES.map((t) => (
                        <TabsTrigger key={t.v} value={t.v} className="rounded-none px-4 py-2 text-sm data-[state=active]:bg-sky-700 data-[state=active]:text-white border-r border-sky-100 last:border-r-0" data-testid={`out-tab-${t.v}`}>{t.label}</TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {data && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <Card label="Total outstanding" value={formatINR(data.totals.outstanding)} cls="text-red-700" big />
                    <Card label="0-30 days" value={formatINR(data.totals["0-30"])} cls="text-stone-700" />
                    <Card label="31-60 days" value={formatINR(data.totals["31-60"])} cls="text-amber-700" />
                    <Card label="61-90 days" value={formatINR(data.totals["61-90"])} cls="text-orange-700" />
                    <Card label="90+ days" value={formatINR(data.totals["90+"])} cls="text-red-700" />
                </div>
            )}

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th></Th><Th>Customer</Th><Th>Type</Th><Th>Phone / City</Th>
                            <Th cls="text-right">0-30</Th><Th cls="text-right">31-60</Th>
                            <Th cls="text-right">61-90</Th><Th cls="text-right">90+</Th>
                            <Th cls="text-right">Total due</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {busy ? (
                            <tr><td colSpan={9} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !rows.length ? (
                            <tr><td colSpan={9} className="p-8 text-center text-emerald-700">All clear — no outstanding bills.</td></tr>
                        ) : rows.map((r, idx) => {
                            const open = expanded[idx];
                            return (
                                <React.Fragment key={idx}>
                                    <tr className="border-b border-stone-100 hover:bg-sky-50/40 cursor-pointer" onClick={() => setExpanded({ ...expanded, [idx]: !open })} data-testid={`out-row-${idx}`}>
                                        <td className="p-2 w-6">{open ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}</td>
                                        <td className="p-2 font-medium text-slate-900">{r.name}</td>
                                        <td className="p-2"><span className="text-[10px] uppercase tracking-widest text-stone-500">{r.customer_type || "—"}</span></td>
                                        <td className="p-2 text-stone-600 text-xs">{r.phone}{r.city ? ` · ${r.city}` : ""}</td>
                                        <td className="p-2 text-right font-mono">{r.buckets["0-30"] ? formatINR(r.buckets["0-30"]) : ""}</td>
                                        <td className="p-2 text-right font-mono text-amber-700">{r.buckets["31-60"] ? formatINR(r.buckets["31-60"]) : ""}</td>
                                        <td className="p-2 text-right font-mono text-orange-700">{r.buckets["61-90"] ? formatINR(r.buckets["61-90"]) : ""}</td>
                                        <td className="p-2 text-right font-mono text-red-700">{r.buckets["90+"] ? formatINR(r.buckets["90+"]) : ""}</td>
                                        <td className="p-2 text-right font-mono text-sky-700 font-medium">{formatINR(r.outstanding)}</td>
                                    </tr>
                                    {open && (
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            <td colSpan={9} className="p-3">
                                                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Bills</div>
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="border-b border-stone-200 text-stone-500">
                                                            <th className="text-left py-1">Invoice #</th><th className="text-left">Date</th><th className="text-left">Age</th>
                                                            <th className="text-right">Total</th><th className="text-right">Paid</th><th className="text-right">Outstanding</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {r.bills.map((b) => (
                                                            <tr key={b.ref_id} className="border-b border-stone-100">
                                                                <td className="py-1 font-mono"><Link to={`/employee/accounts/invoices/${b.ref_id}`} className="text-sky-700 hover:underline">{b.invoice_number}</Link></td>
                                                                <td className="font-mono text-stone-600">{b.date}</td>
                                                                <td><span className={`text-[10px] tracking-widest uppercase ${
                                                                    b.bucket === "90+" ? "text-red-700" :
                                                                    b.bucket === "61-90" ? "text-orange-700" :
                                                                    b.bucket === "31-60" ? "text-amber-700" : "text-stone-600"
                                                                }`}>{b.age_days}d</span></td>
                                                                <td className="text-right font-mono">{formatINR(b.total)}</td>
                                                                <td className="text-right font-mono text-emerald-700">{formatINR(b.paid)}</td>
                                                                <td className="text-right font-mono text-red-700">{formatINR(b.outstanding)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const Th = ({ children, cls = "" }) => <th className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>;
const Card = ({ label, value, cls = "", big = false }) => (
    <div className={`bg-white border border-sky-100 p-4 ${big ? "ring-1 ring-red-200" : ""}`}>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif ${big ? "text-2xl" : "text-xl"} mt-1 ${cls}`}>{value}</div>
    </div>
);
